import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/integrations/db/client";
import type { Tables } from "@/integrations/db/types.generated";
import { useFeature } from "@/hooks/useTenant";
import { useNotificationPreferences } from "@/hooks/useNotificationPreferences";
import { channelOn, type NotificationKind } from "@/lib/notificationKinds";

/**
 * A due-soon, overdue or reminder notification (#9112), with the ticket it is
 * about. Written by the dispatcher, never by the browser; the browser only
 * reads them and marks them read.
 */
export type TicketNotification = Tables<"ticket_notifications"> & {
  kind: Extract<NotificationKind, "due_soon" | "overdue" | "reminder">;
  backlog_item?: Pick<Tables<"backlog_items">, "id" | "task" | "ticket_number" | "deleted_at"> | null;
};

const KEY = ["ticket-notifications"];

export const useTicketNotifications = () => {
  const enabled = useFeature("notifications");
  const { data: prefs = [] } = useNotificationPreferences();

  return useQuery({
    queryKey: KEY,
    enabled,
    queryFn: async (): Promise<TicketNotification[]> => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return [];
      const { data, error } = await supabase
        .from("ticket_notifications")
        .select("*, backlog_item:backlog_items(id, task, ticket_number, deleted_at)")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []).filter((n) => n.backlog_item?.deleted_at === null) as TicketNotification[];
    },
    // A kind switched off in the app is hidden here; its rows may still exist
    // because they are also the email outbox.
    select: (rows) => rows.filter((n) => channelOn(prefs, n.kind, "in_app")),
  });
};

export const useMarkTicketNotificationsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    /** One notification by id, or every unread one with "all". */
    mutationFn: async (which: string | "all") => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const update = supabase
        .from("ticket_notifications")
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq("user_id", user.id);
      const { error } = which === "all" ? await update.eq("is_read", false) : await update.eq("id", which);
      if (error) throw error;
    },
    onMutate: async (which) => {
      await queryClient.cancelQueries({ queryKey: KEY });
      const previous = queryClient.getQueryData<TicketNotification[]>(KEY);
      queryClient.setQueryData<TicketNotification[]>(KEY, (old) =>
        old?.map((n) => (which === "all" || n.id === which ? { ...n, is_read: true } : n)),
      );
      return { previous };
    },
    onError: (_err, _which, context) => {
      if (context?.previous) queryClient.setQueryData(KEY, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
};
